import type {
  LedgerResponse,
  ProjectionResponse,
  DateRangeResponse,
  UsersResponse,
  SettingsResponse,
  MutationResponse,
  SubscriptionFormData,
  UserFormData,
  Settings,
  AuthResponse,
  TodosResponse,
  TodoMutationResponse,
  TodoFormData,
  ExpensesResponse,
  ExpenseMutationResponse,
  ExpenseFormData,
} from '../types';

const API_BASE = '/api';
const AUTH_TOKEN_KEY = 'submgr_auth_token';

export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const response = await fetch(url, {
    ...options,
    cache: options?.cache ?? 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    setAuthToken(null);
    if (token && typeof window !== 'undefined') {
      window.location.assign('/');
    }
  }

  if (!response.ok) {
    let apiError = `HTTP error! status: ${response.status}`;
    try {
      const payload = await response.json() as { error?: string };
      if (payload?.error) {
        apiError = payload.error;
      }
    } catch {}
    throw new Error(apiError);
  }

  return response.json();
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await request<AuthResponse>(`${API_BASE}/auth`, {
    method: 'POST',
    body: JSON.stringify({ action: 'login', email, password }),
  });
  if (response.success && response.token) {
    setAuthToken(response.token);
  }
  return response;
}

export async function logout(): Promise<AuthResponse> {
  const response = await request<AuthResponse>(`${API_BASE}/auth`, {
    method: 'POST',
    body: JSON.stringify({ action: 'logout' }),
  });
  setAuthToken(null);
  return response;
}

export async function getCurrentUser(): Promise<AuthResponse> {
  return request<AuthResponse>(`${API_BASE}/auth`);
}

export async function getAuthStatus(): Promise<AuthResponse> {
  return request<AuthResponse>(`${API_BASE}/auth?status=1`);
}

export async function bootstrapRootUser(
  name: string,
  email: string,
  password: string,
  setupKey?: string,
): Promise<AuthResponse> {
  const response = await request<AuthResponse>(`${API_BASE}/auth`, {
    method: 'POST',
    body: JSON.stringify({ action: 'bootstrap', name, email, password, setupKey }),
  });

  if (response.success && response.token) {
    setAuthToken(response.token);
  }

  return response;
}

export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<AuthResponse> {
  return request<AuthResponse>(`${API_BASE}/auth`, {
    method: 'POST',
    body: JSON.stringify({ action: 'changePassword', currentPassword, newPassword }),
  });
}

// Ledger / Subscriptions
export async function getLedger(month?: string, userId?: string): Promise<LedgerResponse> {
  const params = new URLSearchParams();
  if (month) params.append('month', month);
  if (userId) params.append('userId', userId);
  
  const query = params.toString();
  return request<LedgerResponse>(`${API_BASE}/ledger${query ? `?${query}` : ''}`);
}

export async function getProjection(year?: string, userId?: string): Promise<ProjectionResponse> {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (userId) params.append('userId', userId);
  
  const query = params.toString();
  return request<ProjectionResponse>(`${API_BASE}/projection${query ? `?${query}` : ''}`);
}

export async function getDateRange(
  startDate: string,
  endDate: string,
  userId?: string
): Promise<DateRangeResponse> {
  const params = new URLSearchParams();
  params.append('startDate', startDate);
  params.append('endDate', endDate);
  if (userId) params.append('userId', userId);
  
  return request<DateRangeResponse>(`${API_BASE}/dateRange?${params.toString()}`);
}

export async function addSubscription(data: SubscriptionFormData): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ action: 'add', data }),
  });
}

export async function updateSubscription(
  id: string,
  data: Partial<SubscriptionFormData>
): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update', data: { ...data, id } }),
  });
}

export async function endSubscription(id: string, endDate: string): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ action: 'end', data: { id, endDate } }),
  });
}

export async function deleteSubscription(id: string): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/subscription`, {
    method: 'POST',
    body: JSON.stringify({ action: 'delete', data: { id } }),
  });
}

// Users
export async function getUsers(): Promise<UsersResponse> {
  return request<UsersResponse>(`${API_BASE}/users`);
}

export async function addUser(data: UserFormData): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify({ action: 'add', data }),
  });
}

export async function updateUser(id: string, data: Partial<UserFormData>): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update', data: { ...data, id } }),
  });
}

export async function deleteUser(id: string): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify({ action: 'delete', data: { id } }),
  });
}

export async function setUserPassword(id: string, password: string, setupKey?: string): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/users`, {
    method: 'POST',
    body: JSON.stringify({ action: 'setPassword', data: { id, password, setupKey } }),
  });
}

// Settings
export async function getSettings(): Promise<SettingsResponse> {
  return request<SettingsResponse>(`${API_BASE}/settings`);
}

export async function updateSettings(data: Partial<Settings>): Promise<MutationResponse> {
  return request<MutationResponse>(`${API_BASE}/settings`, {
    method: 'POST',
    body: JSON.stringify({ data }),
  });
}

// Todos
export async function getTodos(): Promise<TodosResponse> {
  return request<TodosResponse>(`${API_BASE}/todos`);
}

export async function addTodo(data: TodoFormData): Promise<TodoMutationResponse> {
  return request<TodoMutationResponse>(`${API_BASE}/todos`, {
    method: 'POST',
    body: JSON.stringify({ action: 'add', data }),
  });
}

export async function updateTodo(id: string, data: Partial<TodoFormData & { completed: boolean }>): Promise<TodoMutationResponse> {
  return request<TodoMutationResponse>(`${API_BASE}/todos`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update', data: { ...data, id } }),
  });
}

export async function toggleTodo(id: string): Promise<TodoMutationResponse> {
  return request<TodoMutationResponse>(`${API_BASE}/todos`, {
    method: 'POST',
    body: JSON.stringify({ action: 'toggle', data: { id } }),
  });
}

export async function deleteTodo(id: string): Promise<TodoMutationResponse> {
  return request<TodoMutationResponse>(`${API_BASE}/todos`, {
    method: 'POST',
    body: JSON.stringify({ action: 'delete', data: { id } }),
  });
}

// Expenses
export async function getExpenses(startDate?: string, endDate?: string): Promise<ExpensesResponse> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const query = params.toString();
  return request<ExpensesResponse>(`${API_BASE}/expenses${query ? `?${query}` : ''}`);
}

export async function addExpense(data: ExpenseFormData): Promise<ExpenseMutationResponse> {
  return request<ExpenseMutationResponse>(`${API_BASE}/expenses`, {
    method: 'POST',
    body: JSON.stringify({ action: 'add', data }),
  });
}

export async function updateExpense(id: string, data: Partial<ExpenseFormData>): Promise<ExpenseMutationResponse> {
  return request<ExpenseMutationResponse>(`${API_BASE}/expenses`, {
    method: 'POST',
    body: JSON.stringify({ action: 'update', data: { ...data, id } }),
  });
}

export async function deleteExpense(id: string): Promise<ExpenseMutationResponse> {
  return request<ExpenseMutationResponse>(`${API_BASE}/expenses`, {
    method: 'POST',
    body: JSON.stringify({ action: 'delete', data: { id } }),
  });
}
